"use client";

import Script from "next/script";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { locales, pathnames } from "@/i18n/navigation";

const BUTTON_FRAME_ID = "thulium-widget-invitation-button-frame";
const FRAME_STYLE_ID = "foodango-chat-widget-style";

const wrapperStyle = `
#thulium-widget-button-frame__wrapper {
  right: 0 !important;
  box-sizing: border-box !important;
  border: 1.53px solid var(--foodango-green-1-200) !important;
  border-right: 0 !important;
  border-radius: 9999px 0 0 9999px !important;
  background: var(--foodango-green-2-300) !important;
}
`;

const hiddenOnMobileStyle = `
@media (width < 768px) {
  #thulium-widget-button-frame__wrapper {
    display: none !important;
  }
}
`;

const HIDE_ON_ALL_MOBILE_ROUTES = true;

const MOBILE_HIDDEN_ROUTES = [
  "/cart",
  "/checkout",
  "/configure-diet",
  "/multidiet-configuration",
];

const MOBILE_HIDDEN_PREFIXES = Object.entries(pathnames)
  .filter(([route]) =>
    MOBILE_HIDDEN_ROUTES.some(
      (hidden) => route === hidden || route.startsWith(`${hidden}/`)
    )
  )
  .flatMap(([route, localized]) => [
    route,
    ...(typeof localized === "string" ? [localized] : Object.values(localized)),
  ])
  .map((path) => path.replace(/\/\[[^\]]+\]$/, ""));

const LOCALE_PREFIX = new RegExp(`^/(${locales.join("|")})(?=/|$)`);

const isHiddenOnMobile = (pathname: string) => {
  const path = pathname.replace(LOCALE_PREFIX, "");

  return MOBILE_HIDDEN_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
};

export const openChatWidget = () => {
  const frame = document.getElementById(
    BUTTON_FRAME_ID
  ) as HTMLIFrameElement | null;

  frame?.contentDocument
    ?.querySelector<HTMLElement>('[class*="invitationButtonBubble"]')
    ?.click();
};

const frameStyle = (iconUrl: string) => `
[class*="invitationButtonBubble"] {
  background: #315146 !important;
  border-radius: 0 !important;
}
svg[class*="invitationIcon"] {
  display: none !important;
}
[class*="bubbleIcon"] {
  width: 37px !important;
  height: 37px !important;
  background: #ffffff;
  -webkit-mask: url("${iconUrl}") center / contain no-repeat;
  mask: url("${iconUrl}") center / contain no-repeat;
}
`;

export const FooterScripts = () => {
  const pathname = usePathname();

  useEffect(() => {
    const iconUrl = `${window.location.origin}/icons/chat-bubble.svg`;

    const styleFrame = () => {
      const frame = document.getElementById(
        BUTTON_FRAME_ID
      ) as HTMLIFrameElement | null;
      const frameDocument = frame?.contentDocument;

      if (!frameDocument || frameDocument.getElementById(FRAME_STYLE_ID)) {
        return;
      }

      const style = frameDocument.createElement("style");
      style.id = FRAME_STYLE_ID;
      style.textContent = frameStyle(iconUrl);
      frameDocument.head.appendChild(style);
    };

    styleFrame();

    const observer = new MutationObserver(styleFrame);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{wrapperStyle}</style>
      {(HIDE_ON_ALL_MOBILE_ROUTES || isHiddenOnMobile(pathname)) && (
        <style>{hiddenOnMobileStyle}</style>
      )}
      <Script
        src="https://cdn.thulium.com/apps/chat-widget/chat-loader.js?hash=goodspeed-494da50f-03de-47c7-8f15-8b15c84be0c2"
        strategy="lazyOnload"
      />
    </>
  );
};
